/**
 * Utilidades para manejo de archivos en modo offline
 */

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export interface FileStorageData {
  base64: string;
  filename: string;
  mimeType: string;
  size: number;
  timestamp: number;
}

/**
 * Convierte un archivo a base64
 */
export const fileToBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Convierte base64 a File
 */
export const base64ToFile = (base64: string, filename: string, mimeType: string): File => {
  const arr = base64.split(',');
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mimeType });
};

/**
 * Verifica si un archivo puede almacenarse offline
 */
export const canStoreOffline = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

/**
 * Prepara un archivo para almacenamiento offline
 */
export const prepareFileForStorage = async (file: File): Promise<FileStorageData | null> => {
  if (!canStoreOffline(file)) {
    return null;
  }

  const base64 = await fileToBase64(file);
  
  return {
    base64,
    filename: file.name,
    mimeType: file.type,
    size: file.size,
    timestamp: Date.now(),
  };
};

/**
 * Recupera un archivo desde el almacenamiento offline
 */
export const restoreFileFromStorage = (data: FileStorageData): File => {
  return base64ToFile(data.base64, data.filename, data.mimeType);
};

/**
 * Formatea el tamaño del archivo en formato legible
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Comprime una imagen si es necesario
 */
export const compressImageIfNeeded = async (file: File): Promise<File> => {
  // Solo comprimir imágenes
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Si es menor a 2MB, no comprimir
  if (file.size <= 2 * 1024 * 1024) {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Reducir a máximo 1920px en el lado más largo
        const maxSize = 1920;
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.85
        );
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

export const MAX_OFFLINE_FILE_SIZE = MAX_FILE_SIZE;
